import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Ban, Briefcase, Calendar, ChevronLeft, Folder, Link, MoreHorizontal, PauseCircle, Pencil, Play, RotateCcw, Star } from 'lucide-react';
import { Avatar, AvatarStack } from '@/components/ui/Avatar';
import { Tag } from '@/components/ui/badges';
import { useToast } from '@/components/ui/toast';
import { daysSince, fmtMoneyExact } from '@/lib/domain/format';
import { legalStatusByApiName } from '@/lib/domain/legalStatus';
import { BOARD_STAGES, effectiveStageEnteredAtUtc, stageOrder } from '@/lib/domain/stages';
import { useHandoff } from '@/features/portfolio/handoffStore';
import { CommitteesSection } from '@/features/committees/CommitteesSection';
import { useCommitteeSchedule } from '@/features/committees/committeeScheduleStore';
import { TasksSection } from '@/features/tasks/TasksSection';
import { useTasks } from '@/features/tasks/api';
import { TimelineSection } from '@/features/timeline/TimelineSection';
import { useTimeline } from '@/features/timeline/api';
import { DealEditForm } from './DealEditForm';
import { useDealDrawer } from './dealDrawerStore';
import { useNewDeal } from './newDealStore';
import { useCriticalTransition } from './criticalTransitionStore';
import { useReferenceData } from './useReferenceData';
import { useOriginators, usePeople } from './useEntities';
import { type Deal, readApiETag } from './types';
import { useDeal } from './useDeal';
import { useDeals } from './useDeals';
import { useMoveDealStage } from './useMoveDealStage';
import { usePauseDeal } from './usePauseDeal';
import { useResumeDeal } from './useResumeDeal';
import { useToggleDealPriority } from './useToggleDealPriority';

const AUX_LABEL: Record<string, string> = {
  Active: 'Ativo',
  Paused: 'Em pausa',
  Discarded: 'Descartado',
  ClosedToPortfolio: 'No portfólio',
};

/**
 * Drawer da ficha do Deal (E6, v1 read-only). Abre pela store global (kanban/tabela/sidebar).
 * Renderiza na hora com o Deal da listagem e enriquece (resumo/tags/pasta) via GET /api/deals/{id}.
 * Edição, timeline e tarefas vêm na sequência.
 */
export function DealDrawer() {
  const toast = useToast();
  const storeDeal = useDealDrawer((s) => s.deal);
  const close = useDealDrawer((s) => s.closeDeal);
  const { data } = useDeals();
  const togglePriority = useToggleDealPriority();
  const move = useMoveDealStage();
  const pause = usePauseDeal();
  const resume = useResumeDeal();
  const openDiscard = useCriticalTransition((s) => s.openDiscard);
  const openReactivate = useCriticalTransition((s) => s.openReactivate);
  const openRetorno = useCriticalTransition((s) => s.openRetorno);
  const openHandoff = useHandoff((s) => s.open);
  const requestSchedule = useCommitteeSchedule((s) => s.requestSchedule);
  // Resolução dos Vínculos da aba Ficha (originadora/pessoa/originador interno). Gateados pelo deal
  // aberto p/ não disparar as listas em todo page load (mesmo padrão de `enabled` do +Novo Deal).
  const refData = useReferenceData();
  const originatorsQ = useOriginators(!!storeDeal?.id);
  const peopleQ = usePeople(!!storeDeal?.id);

  // Usa a versão "viva" da listagem (reflete mover-fase/prioritário otimistas com o drawer aberto).
  // `useDeals()` só cobre status Active (default) — para um deal Pausado/Descartado/no Portfólio,
  // ou um deal fabricado pela busca global (GlobalSearch.dealFromHit, com status/etag/datas
  // inventados), listDeal fica undefined e o snapshot do clique (storeDeal) é a única fonte.
  const listDeal = data?.deals.find((x) => x.id === storeDeal?.id);
  const baseDeal = listDeal ?? storeDeal;
  const detail = useDeal(baseDeal?.id ?? null);
  // Funde o detalhe (GET /deals/{id} — sempre atual, não restrito a status) sobre a base acima
  // (achado F2/F11, revisão 2026-07): sem isto, um deal fora do Active ou vindo da busca global
  // mostrava status/aging errados e disparava ações (pausar, mover, prioritário) com ETag vazio ou
  // desatualizado. Nomes resolvidos (leadUserName/companyName/thesisNames/teamUserNames) só existem
  // na listagem — o detalhe não os expõe — então ficam da base; os demais campos, quando o detalhe
  // já carregou, vêm dele.
  const dealDetail = detail.data;
  const deal: Deal | null = !baseDeal
    ? null
    : !dealDetail
      ? baseDeal
      : {
          ...baseDeal,
          code: dealDetail.code,
          codename: dealDetail.codename,
          stage: dealDetail.currentStage,
          auxiliaryStatus: dealDetail.auxiliaryStatus,
          legalStatus: dealDetail.legalStatus,
          stageEnteredAtUtc: dealDetail.stageEnteredAtUtc,
          initialEntryAtUtc: dealDetail.initialEntryAtUtc,
          leadUserId: dealDetail.leadUserId,
          investmentValueBRL: dealDetail.investmentValueBRL,
          checkDiscretionaryBRL: dealDetail.checkDiscretionaryBRL,
          checkCoinvestmentBRL: dealDetail.checkCoinvestmentBRL,
          isPriority: dealDetail.isPriority,
          companyId: dealDetail.targetCompanyId,
          etag: readApiETag(dealDetail),
        };
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<'ficha' | 'timeline' | 'tarefas'>('ficha');

  // Contagens para os rótulos das abas. Mesmas queryKeys das seções → React Query deduplica (sem
  // fetch dobrado); `useTasks` gateado por deal p/ não buscar todas as tarefas em todo page load.
  const timelineQ = useTimeline('Deal', deal?.id ?? null);
  const tasksQ = useTasks({ linkType: 'Deal', linkId: deal?.id ?? null }, !!deal?.id);
  const timelineCount = timelineQ.data?.length ?? 0;
  const taskCount = tasksQ.data?.items.length ?? 0;

  // Sai do modo de edição e volta para a aba Ficha ao trocar de deal (ou fechar).
  useEffect(() => {
    setEditing(false);
    setTab('ficha');
  }, [storeDeal?.id]);

  // Achado F3 (revisão 2026-07): com um modal empilhado por cima (Handoff, transição crítica,
  // +Novo Deal), o Esc antes fechava os DOIS ao mesmo tempo — nenhum `stopPropagation` teria efeito
  // aqui (múltiplos listeners no MESMO `window` disparam independentemente da propagação entre nós
  // da árvore). Fix: este handler verifica se há camada acima aberta e, se houver, não age — quem
  // fecha é o handler do próprio modal do topo.
  const criticalOpen = useCriticalTransition((s) => s.open);
  const handoffDeal = useHandoff((s) => s.deal);
  const newDealOpen = useNewDeal((s) => s.open);
  useEffect(() => {
    if (!storeDeal) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (criticalOpen || handoffDeal || newDealOpen) return; // camada acima trata o próprio Esc
      // Em edição, Esc cancela a edição; fora dela, fecha a ficha.
      if (editing) setEditing(false);
      else close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [storeDeal, close, editing, criticalOpen, handoffDeal, newDealOpen]);

  if (!deal) return null;

  const stageIdx = BOARD_STAGES.findIndex((s) => s.apiName === deal.stage);
  const legal = legalStatusByApiName(deal.legalStatus);
  const aging = daysSince(effectiveStageEnteredAtUtc(deal));
  const d = detail.data;
  const isDiscarded = deal.auxiliaryStatus === 'Discarded';
  const isPaused = deal.auxiliaryStatus === 'Paused';
  const isActive = deal.auxiliaryStatus === 'Active';

  // Vínculos resolvidos (Section "Vínculos" da Ficha). Sob noUncheckedIndexedAccess: ?. + ?? '—'.
  const originator = d?.originatorIdSnapshot
    ? originatorsQ.data?.items.find((o) => o.id === d.originatorIdSnapshot)
    : undefined;
  const originatorTypeName = refData.data?.originatorTypes.find((t) => t.id === originator?.typeId)?.name;
  const originatorPerson = d?.originatorPersonIdSnapshot
    ? peopleQ.data?.items.find((p) => p.id === d.originatorPersonIdSnapshot)
    : undefined;
  const internalOriginator = d?.internalOriginatorId
    ? (refData.data?.users.find((u) => u.id === d.internalOriginatorId) ??
       refData.data?.leadUsers.find((u) => u.id === d.internalOriginatorId))
    : undefined;

  // Stepper clicável: avanço move direto; retorno (fase anterior) abre o modal de comentário.
  const handleStageClick = (apiName: string) => {
    if (apiName === deal.stage) return;
    if (stageOrder(apiName) < stageOrder(deal.stage)) openRetorno(deal, apiName);
    else move.mutate({ id: deal.id, toStage: apiName, etag: deal.etag });
  };

  return (
    <>
      <button
        type="button"
        aria-label="Fechar ficha"
        onClick={close}
        className="fixed inset-0 z-50 cursor-default bg-[rgba(20,18,12,0.35)]"
      />
      <aside
        role="dialog"
        aria-label={`Ficha do deal ${deal.codename || deal.companyName}`}
        className="fixed inset-y-0 right-0 z-[60] flex w-[min(960px,92vw)] flex-col bg-vega-surface shadow-vega-lg"
      >
        <div className="border-b border-vega-line px-6 pt-3.5">
          <div className="mb-2 flex items-center gap-2.5">
            <button
              type="button"
              onClick={close}
              aria-label="Fechar"
              className="-ml-1 inline-flex rounded p-1 text-vega-muted hover:bg-vega-bg-2"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="font-mono text-[11px] tracking-wide text-vega-muted-2">{deal.code}</span>
            <Tag size="sm">{AUX_LABEL[deal.auxiliaryStatus] ?? deal.auxiliaryStatus}</Tag>
            {legal?.cardTag && (
              <Tag tone="warn" size="sm">
                {legal.cardTag}
              </Tag>
            )}
            <div className="flex-1" />
            {!editing && (
              <>
                <button
                  type="button"
                  onClick={() => togglePriority.mutate({ id: deal.id })}
                  disabled={togglePriority.isPending}
                  className="inline-flex items-center gap-1.5 rounded border border-vega-line px-2 py-1 text-[11px] disabled:opacity-60"
                  style={{ color: deal.isPriority ? 'var(--vega-gold)' : 'var(--vega-muted)' }}
                >
                  <Star size={12} fill={deal.isPriority ? 'currentColor' : 'none'} />
                  {deal.isPriority ? 'Prioritário' : 'Marcar prioritário'}
                </button>
                <DealActionsMenu
                  isActive={isActive}
                  isPaused={isPaused}
                  isDiscarded={isDiscarded}
                  folderId={d?.folderId}
                  editDisabled={!detail.data}
                  onSchedule={() => {
                    setTab('ficha');
                    requestSchedule();
                  }}
                  onPause={() => pause.mutate({ id: deal.id, etag: deal.etag })}
                  onResume={() => resume.mutate({ id: deal.id, etag: deal.etag })}
                  onHandoff={() => openHandoff(deal)}
                  onEdit={() => setEditing(true)}
                  onDiscard={() => openDiscard(deal)}
                  onReactivate={() => openReactivate(deal)}
                />
              </>
            )}
          </div>

          <div className="mb-1 flex items-baseline gap-3">
            <h1 className="text-[22px] font-semibold tracking-tight text-vega-ink">
              {deal.codename || deal.companyName}
            </h1>
            <span className="text-sm text-vega-muted">· {deal.companyName}</span>
          </div>

          <div className="mb-3.5 flex items-center gap-4 text-[11.5px] text-vega-muted">
            <span>{aging} dias nesta fase</span>
            {d?.folderId && (
              <span className="inline-flex items-center gap-1.5">
                <Folder size={12} /> Pasta: <span className="font-mono text-vega-ink-2">{d.folderId}</span>
              </span>
            )}
          </div>
        </div>

        <div className="border-b border-vega-line px-6 py-3.5">
          <div className="flex overflow-hidden rounded border border-vega-line-2 bg-vega-bg-2">
            {BOARD_STAGES.map((s, i) => {
              const active = i === stageIdx;
              const done = i < stageIdx;
              const tone = active
                ? 'bg-vega-ink text-vega-bg'
                : done
                  ? 'bg-vega-surface text-vega-ink-2'
                  : 'text-vega-muted-2';
              return (
                <button
                  type="button"
                  key={s.apiName}
                  onClick={() => handleStageClick(s.apiName)}
                  disabled={move.isPending || active || editing || !isActive}
                  title={!isActive ? 'Deal não está ativo' : active ? 'Fase atual' : `Mover para ${s.label}`}
                  className={`flex flex-1 flex-col items-start gap-0.5 px-2 py-2 text-left enabled:hover:brightness-95 disabled:cursor-default ${
                    i < BOARD_STAGES.length - 1 ? 'border-r border-vega-line-2' : ''
                  } ${tone}`}
                >
                  <span className="font-mono text-[9px] opacity-70">{String(i + 1).padStart(2, '0')}</span>
                  <span className="text-[10.5px] font-medium leading-tight">{s.short}</span>
                </button>
              );
            })}
          </div>
        </div>

        {!editing && (
          <div className="flex gap-0 border-b border-vega-line px-6">
            {(
              [
                ['ficha', 'Ficha'],
                ['timeline', `Timeline (${timelineCount})`],
                ['tarefas', `Tarefas (${taskCount})`],
              ] as const
            ).map(([id, label]) => (
              <button
                type="button"
                key={id}
                onClick={() => setTab(id)}
                className={`-mb-px border-b-2 px-3.5 py-2 text-[12.5px] font-medium ${
                  tab === id ? 'border-vega-ink text-vega-ink' : 'border-transparent text-vega-muted'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {editing && detail.data ? (
            <DealEditForm
              deal={detail.data}
              onDone={() => setEditing(false)}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <>
            <div className={tab === 'ficha' ? 'px-6 py-5' : 'hidden'}>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_280px]">
                <div>
          <Section title="Resumo da tese">
            {detail.isLoading ? (
              <p className="text-[13px] text-vega-muted-2">Carregando…</p>
            ) : (
              <p className="whitespace-pre-line text-[13px] leading-relaxed text-vega-ink-2">
                {d?.thesisSummary?.trim() || '—'}
              </p>
            )}
          </Section>

          <Section title="Identificação">
            <InfoRow label="Código">
              <span className="font-mono">{deal.code}</span>
            </InfoRow>
            <InfoRow label="Codinome">{deal.codename || '—'}</InfoRow>
            <InfoRow label="Tese">
              {deal.thesisNames.length > 0 ? (
                <span className="inline-flex flex-wrap gap-1">
                  {deal.thesisNames.map((t) => (
                    <Tag key={t} size="sm">
                      {t}
                    </Tag>
                  ))}
                </span>
              ) : (
                '—'
              )}
            </InfoRow>
            <InfoRow label="Status processual">{legal?.label ?? deal.legalStatus}</InfoRow>
            {d && d.tags.length > 0 && (
              <InfoRow label="Tags">
                <span className="inline-flex flex-wrap gap-1">
                  {d.tags.map((t) => (
                    <Tag key={t} size="sm">
                      {t}
                    </Tag>
                  ))}
                </span>
              </InfoRow>
            )}
          </Section>

          <Section title="Vínculos">
            <InfoRow label="Empresa-alvo">{deal.companyName}</InfoRow>
            <InfoRow label="Originadora">
              {!d?.originatorIdSnapshot ? (
                <span className="text-vega-muted">Originação interna</span>
              ) : originator ? (
                <span className="inline-flex items-center gap-1.5">
                  {originator.name}
                  {originatorTypeName && <Tag size="sm">{originatorTypeName}</Tag>}
                </span>
              ) : (
                '—'
              )}
            </InfoRow>
            <InfoRow label="Pessoa originadora">{originatorPerson?.name ?? '—'}</InfoRow>
            <InfoRow label="Originador interno">
              {internalOriginator ? (
                <span className="inline-flex items-center gap-2">
                  <Avatar name={internalOriginator.name} colorKey={internalOriginator.id} size={18} />{' '}
                  {internalOriginator.name}
                </span>
              ) : (
                '—'
              )}
            </InfoRow>
          </Section>

          <Section title="Time">
            <InfoRow label="Responsável">
              <span className="inline-flex items-center gap-2">
                <Avatar name={deal.leadUserName} colorKey={deal.leadUserId} size={18} /> {deal.leadUserName}
              </span>
            </InfoRow>
            <InfoRow label="Equipe">
              {deal.teamUserNames.length > 0 ? (
                <span className="inline-flex items-center gap-2">
                  <AvatarStack names={deal.teamUserNames} max={5} size={18} />
                  <span className="text-vega-muted">{deal.teamUserNames.join(', ')}</span>
                </span>
              ) : (
                '—'
              )}
            </InfoRow>
          </Section>
                </div>

                <div>
          <Section title="Dimensão financeira">
            <div className="rounded border border-vega-line-2 bg-vega-surface-2 px-3.5 py-3">
              <div className="mb-1 text-[10.5px] uppercase tracking-wide text-vega-muted">
                Cheque total
              </div>
              <div className="font-mono text-[22px] font-semibold tracking-tight text-vega-ink">
                {fmtMoneyExact(deal.investmentValueBRL) || '—'}
              </div>
              {(d?.checkDiscretionaryBRL != null || d?.checkCoinvestmentBRL != null) && (
                <div className="mt-2.5 flex gap-6 border-t border-vega-line-2 pt-2.5">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-vega-muted">Discricionário</div>
                    <div className="font-mono text-[13px] font-semibold text-vega-ink">
                      {fmtMoneyExact(d?.checkDiscretionaryBRL ?? 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-vega-muted">Coinvestimento</div>
                    <div className="font-mono text-[13px] font-semibold text-vega-ink">
                      {fmtMoneyExact(d?.checkCoinvestmentBRL ?? 0)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Section>

          <CommitteesSection dealId={deal.id} />

          {d?.folderId && (
            <Section title="Pasta na rede">
              <button
                type="button"
                onClick={() => {
                  const folderId = d.folderId;
                  if (!folderId) return;
                  void navigator.clipboard?.writeText(folderId);
                  toast('Caminho da pasta copiado');
                }}
                className="flex w-full items-center gap-2 rounded border border-vega-line-2 bg-vega-surface-2 px-2.5 py-2 text-left hover:bg-vega-bg-2"
              >
                <Folder size={14} className="shrink-0 text-vega-muted" />
                <span className="truncate font-mono text-[11px] text-vega-ink-2">{d.folderId}</span>
                <Link size={11} className="ml-auto shrink-0 text-vega-muted-2" />
              </button>
            </Section>
          )}
                </div>
              </div>
            </div>

            <div className={tab === 'timeline' ? 'px-6 py-5' : 'hidden'}>
              <TimelineSection linkType="Deal" linkId={deal.id} />
            </div>

            <div className={tab === 'tarefas' ? 'px-6 py-5' : 'hidden'}>
              <TasksSection linkType="Deal" linkId={deal.id} />
            </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-6">
      <h3 className="mb-2 border-b border-vega-line pb-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-vega-muted">
        {title}
      </h3>
      {children}
    </section>
  );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 border-b border-vega-line-2 py-2 text-[12.5px]">
      <div className="pt-0.5 text-[11px] uppercase tracking-wide text-vega-muted">{label}</div>
      <div className="text-vega-ink">{children}</div>
    </div>
  );
}

/** Fecha o menu ao clicar fora ou apertar Esc (mesmo padrão do UserMenu.tsx / Select.tsx). */
function useOutsideClose(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onMouse = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  return ref;
}

/** Item do menu "Ações do Deal" (espelha o MenuItem do UserMenu, com suporte a `disabled`). */
function MenuItem({
  onClick,
  icon,
  danger = false,
  disabled = false,
  children,
}: {
  onClick: () => void;
  icon: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-[12.5px] enabled:hover:bg-vega-bg-2 disabled:opacity-50 ${
        danger ? 'text-vega-danger' : 'text-vega-ink-2'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

/**
 * Menu "..." de ações do Deal (mock deal-drawer.jsx — DealHeader). Substitui os botões inline do
 * header por um dropdown alinhado à direita. Itens condicionais ao estado (ativo/pausa/descartado) e
 * à presença de pasta; "Editar ficha" segue o gate de carregamento do detalhe. Padrão local (não
 * compartilha com UserMenu — extração comum é follow-up).
 */
function DealActionsMenu({
  isActive,
  isPaused,
  isDiscarded,
  folderId,
  editDisabled,
  onSchedule,
  onPause,
  onResume,
  onHandoff,
  onEdit,
  onDiscard,
  onReactivate,
}: {
  isActive: boolean;
  isPaused: boolean;
  isDiscarded: boolean;
  folderId?: string | null;
  editDisabled: boolean;
  onSchedule: () => void;
  onPause: () => void;
  onResume: () => void;
  onHandoff: () => void;
  onEdit: () => void;
  onDiscard: () => void;
  onReactivate: () => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const ref = useOutsideClose(open, () => setOpen(false));

  // Fecha o menu e dispara a ação (padrão close()+ação do mock).
  const run = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  const copyFolder = run(() => {
    void navigator.clipboard?.writeText(folderId ?? '');
    toast('Caminho da pasta copiado');
  });
  const copyLink = run(() => {
    void navigator.clipboard?.writeText(window.location.href);
    toast('Link copiado');
  });

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Ações do deal"
        className="inline-flex items-center rounded border border-vega-line px-2 py-1 text-vega-muted hover:bg-vega-bg-2"
      >
        <MoreHorizontal size={14} />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Ações do Deal"
          className="absolute right-0 top-[calc(100%+6px)] z-[70] w-56 overflow-hidden rounded-md border border-vega-line bg-vega-surface shadow-vega-lg"
        >
          <div className="border-b border-vega-line px-3 py-2 text-[10.5px] font-semibold uppercase tracking-wide text-vega-muted">
            Ações do Deal
          </div>
          <div className="p-1">
            <MenuItem icon={<Calendar size={13} strokeWidth={1.6} />} onClick={run(onSchedule)}>
              Agendar comitê
            </MenuItem>
            {folderId && (
              // Achado F10: o handler só copia o caminho (abrir file:// é bloqueado pelo browser
              // mesmo) — rótulo agora reflete a ação real.
              <MenuItem icon={<Folder size={13} strokeWidth={1.6} />} onClick={copyFolder}>
                Copiar caminho da pasta
              </MenuItem>
            )}
            <MenuItem icon={<Link size={13} strokeWidth={1.6} />} onClick={copyLink}>
              Copiar link do deal
            </MenuItem>

            <div className="my-1 h-px bg-vega-line" />

            {isActive && (
              <MenuItem icon={<PauseCircle size={13} strokeWidth={1.6} />} onClick={run(onPause)}>
                Pausar deal
              </MenuItem>
            )}
            {isPaused && (
              <MenuItem icon={<Play size={13} strokeWidth={1.6} />} onClick={run(onResume)}>
                Retomar deal
              </MenuItem>
            )}
            {!isDiscarded && (
              <MenuItem icon={<Briefcase size={13} strokeWidth={1.6} />} onClick={run(onHandoff)}>
                Mover para Portfólio
              </MenuItem>
            )}
            <MenuItem
              icon={<Pencil size={13} strokeWidth={1.6} />}
              disabled={editDisabled}
              onClick={run(onEdit)}
            >
              Editar ficha
            </MenuItem>

            <div className="my-1 h-px bg-vega-line" />

            {isDiscarded ? (
              <MenuItem icon={<RotateCcw size={13} strokeWidth={1.6} />} onClick={run(onReactivate)}>
                Reativar deal
              </MenuItem>
            ) : (
              <MenuItem icon={<Ban size={13} strokeWidth={1.6} />} danger onClick={run(onDiscard)}>
                Descartar deal…
              </MenuItem>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
